// Live Colour Tiles motion (DESIGN.md, Motion): transform and opacity only, so it stays smooth on
// budget phones; the native driver on Android. Every effect has a reduced-motion path that keeps the
// state change (a short fade) and drops the movement.
import { type ReactNode, useEffect, useState } from "react";
import {
  AccessibilityInfo,
  Animated,
  Easing,
  Platform,
  type StyleProp,
  type ViewStyle,
} from "react-native";

const native = Platform.OS !== "web";

/** Confident arrival: fast start, long soft landing. */
export const easeOutExpo = Easing.out(Easing.exp);

/** Follows the system "reduce motion" setting, live. */
export function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    let live = true;
    void AccessibilityInfo.isReduceMotionEnabled().then((on) => {
      if (live) setReduced(on);
    });
    const sub = AccessibilityInfo.addEventListener(
      "reduceMotionChanged",
      setReduced,
    );
    return () => {
      live = false;
      // Some environments (test mocks, older web shims) return no subscription.
      sub?.remove?.();
    };
  }, []);
  return reduced;
}

/** Staggers are capped so the last tile never waits long. */
const STEP_MS = 55;
const MAX_STEPS = 6;

/**
 * The tile world's arrival: a tile turns in about its left edge from a slight angle and rises into
 * place, one after another (the metro start-screen turnstile). Reduced motion: a 200 ms fade.
 * Content is laid out from the first frame; only its transform and opacity animate.
 */
export function Reveal({
  index = 0,
  children,
  style,
  from = "turn",
}: {
  /** Position in the sequence; sets the delay. */
  index?: number;
  children: ReactNode;
  style?: StyleProp<ViewStyle>;
  /** `turn` for tiles, `slide` for type that glides in from the right. */
  from?: "turn" | "slide";
}) {
  const reduced = useReducedMotion();
  const [progress] = useState(() => new Animated.Value(0));
  useEffect(() => {
    const anim = Animated.timing(progress, {
      toValue: 1,
      duration: reduced ? 200 : 620,
      delay: reduced ? 0 : Math.min(index, MAX_STEPS) * STEP_MS,
      easing: easeOutExpo,
      useNativeDriver: native,
    });
    anim.start();
    return () => anim.stop();
  }, [progress, index, reduced]);

  const opacity = progress.interpolate({
    inputRange: [0, 0.4, 1],
    outputRange: [0, 1, 1],
  });
  const transform = reduced
    ? []
    : from === "slide"
      ? [
          {
            translateX: progress.interpolate({
              inputRange: [0, 1],
              outputRange: [40, 0],
            }),
          },
        ]
      : [
          { perspective: 1000 },
          {
            translateY: progress.interpolate({
              inputRange: [0, 1],
              outputRange: [24, 0],
            }),
          },
          {
            rotateY: progress.interpolate({
              inputRange: [0, 1],
              outputRange: ["-24deg", "0deg"],
            }),
          },
        ];
  return (
    <Animated.View style={[style, { opacity, transform }]}>
      {children}
    </Animated.View>
  );
}

/**
 * Press feedback for tiles: the tile sinks a little under the finger and springs back on release.
 * Reduced motion: no scale (the press opacity and ripple still confirm it).
 */
export function usePressSink() {
  const reduced = useReducedMotion();
  const [scale] = useState(() => new Animated.Value(1));
  const to = (value: number) =>
    Animated.spring(scale, {
      toValue: value,
      speed: 40,
      bounciness: value === 1 ? 6 : 0,
      useNativeDriver: native,
    }).start();
  return {
    style: { transform: [{ scale }] },
    onPressIn: () => {
      if (!reduced) to(0.96);
    },
    onPressOut: () => to(1),
  };
}

/** A loading placeholder that breathes while it waits; still under reduced motion. */
export function useBreathing(active: boolean) {
  const reduced = useReducedMotion();
  const [level] = useState(() => new Animated.Value(1));
  useEffect(() => {
    if (!active || reduced) return undefined;
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(level, {
          toValue: 0.55,
          duration: 700,
          easing: Easing.inOut(Easing.quad),
          useNativeDriver: native,
        }),
        Animated.timing(level, {
          toValue: 1,
          duration: 700,
          easing: Easing.inOut(Easing.quad),
          useNativeDriver: native,
        }),
      ]),
    );
    loop.start();
    return () => {
      loop.stop();
      level.setValue(1);
    };
  }, [active, reduced, level]);
  return { opacity: level };
}
